import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LoaderCircle } from 'lucide-react';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { toast } from 'sonner';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { PasswordCriteriaList, getPasswordCriteria } from 'components/ui/PasswordCriteriaList';
import { emailSchema, resetPasswordSchema } from 'schemas/changePasswordSchema';

type EmailFormValues = z.infer<typeof emailSchema>;
type ResetFormValues = z.infer<typeof resetPasswordSchema>;

type Props = {
  onBack: () => void;
};

export const ForgotPassword = ({ onBack }: Props) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const resetForm = useForm<ResetFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
    defaultValues: { code: '', newPassword: '', confirmPassword: '' },
  });

  const newPasswordValue = resetForm.watch('newPassword');
  const confirmPasswordValue = resetForm.watch('confirmPassword');
  const allCriteriaMet = Object.values(getPasswordCriteria(newPasswordValue)).every(Boolean);
  const passwordsMatch = newPasswordValue === confirmPasswordValue;

  const handleSendCode = async ({ email }: EmailFormValues) => {
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setEmail(email);
      resetForm.reset();
      setCodeSent(true);
      toast.success('Verification code sent to your email');
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async ({ code, newPassword }: ResetFormValues) => {
    setLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      toast.success('Password reset successful! Please log in with your new password.');
      onBack();
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    emailForm.reset({ email });
    resetForm.reset();
    setCodeSent(false);
  };

  if (!codeSent) {
    return (
      <form className="space-y-5" onSubmit={emailForm.handleSubmit(handleSendCode)}>
        <p className="text-sm text-slate-500">
          Enter your email address and we'll send you a verification code to reset your password.
        </p>

        <div className="space-y-2">
          <Label htmlFor="forgot-email">Email</Label>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            placeholder="Enter your email"
            disabled={loading}
            {...emailForm.register('email')}
          />
          {emailForm.formState.errors.email && (
            <p className="text-xs text-red-500">{emailForm.formState.errors.email.message}</p>
          )}
        </div>

        <div className="space-y-3">
          <Button
            type="submit"
            className="h-12 w-full rounded-2xl text-base font-semibold"
            disabled={loading}
          >
            {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            {loading ? 'Sending...' : 'Send Verification Code'}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="h-12 w-full rounded-2xl text-base"
            disabled={loading}
            onClick={onBack}
          >
            Back to Login
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="max-h-[calc(100svh-260px)] overflow-y-auto">
      <form className="space-y-5 p-2" onSubmit={resetForm.handleSubmit(handleResetPassword)}>
        <p className="text-sm text-slate-500">
          Enter the verification code sent to{' '}
          <span className="font-medium text-slate-700">{email}</span> and choose a new password.
        </p>

        <div className="space-y-2">
          <Label htmlFor="code">Verification Code</Label>
          <Input
            id="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Enter verification code"
            disabled={loading}
            {...resetForm.register('code')}
          />
          {resetForm.formState.errors.code && (
            <p className="text-xs text-red-500">{resetForm.formState.errors.code.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="newPassword">New Password</Label>
          <Input
            id="newPassword"
            isPassword
            placeholder="Enter new password"
            disabled={loading}
            {...resetForm.register('newPassword')}
          />
          {resetForm.formState.errors.newPassword && (
            <p className="text-xs text-red-500">{resetForm.formState.errors.newPassword.message}</p>
          )}
        </div>

        <PasswordCriteriaList password={newPasswordValue} />

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            id="confirmPassword"
            isPassword
            placeholder="Confirm new password"
            disabled={loading}
            {...resetForm.register('confirmPassword')}
          />
          {resetForm.formState.errors.confirmPassword && (
            <p className="text-xs text-red-500">
              {resetForm.formState.errors.confirmPassword.message}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Button
            type="submit"
            className="h-12 w-full rounded-2xl text-base font-semibold"
            disabled={!allCriteriaMet || !passwordsMatch || !confirmPasswordValue || loading}
          >
            {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="h-12 w-full rounded-2xl text-base"
            disabled={loading}
            onClick={handleResendCode}
          >
            Resend Code
          </Button>
        </div>
      </form>
    </div>
  );
};
